import React, { useEffect, useState } from "react";
import PageHeader from "../components/PageHeader";
import { authApi, getSiteConfig } from "../services/api";

const hasExtension = () => Boolean(document.documentElement.dataset.plateoExtension);

function ConnectExtension() {
  const [installed, setInstalled] = useState(hasExtension);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const [stale, setStale] = useState(() => document.documentElement.dataset.plateoExtension === "stale");
  const [extensionUrl, setExtensionUrl] = useState("");

  useEffect(() => {
    getSiteConfig().then((c) => setExtensionUrl(c.extensionUrl));
  }, []);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.source !== window || event.origin !== window.location.origin) return;
      if (event.data?.type === "PLATEO_PONG") setInstalled(true);
      if (event.data?.type === "PLATEO_CONNECTED") setState("connected");
      if (event.data?.type === "PLATEO_STALE") setStale(true);
    };
    window.addEventListener("message", onMessage);
    window.postMessage({ type: "PLATEO_PING" }, window.location.origin);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const connect = async () => {
    setState("connecting");
    setError("");
    try {
      const { token } = await authApi.extensionToken();
      window.postMessage({ type: "PLATEO_CONNECT", token }, window.location.origin);
      setTimeout(() => setState((s) => (s === "connecting" ? "timeout" : s)), 3000);
    } catch (err) {
      setError(err.message);
      setState("idle");
    }
  };

  return (
    <>
      <PageHeader
        title="Chrome extension"
        subtitle="Save jobs from any site and fill in applications with your profile, resume and AI-written answers."
      />
      <div className="card" style={{ maxWidth: 640 }}>
        <div className="card-body">
          {state === "connected" ? (
            <div className="text-center py-3">
              <div className="success-icon mb-3">
                <i className="bi bi-plug" />
              </div>
              <h2 className="h5">Extension connected</h2>
              <p className="text-muted small mb-0">
                Open any job posting or application and click the Plateo icon in Chrome's toolbar. You can close this tab.
              </p>
            </div>
          ) : installed ? (
            <>
              <h2 className="h6">Connect the extension to your account</h2>
              <p className="text-muted small">
                The extension gets its own sign-in that lasts 30 days. You can disconnect it any time from its settings.
              </p>
              {error && <div className="alert alert-danger py-2 small">{error}</div>}
              {stale && (
                <div className="alert alert-warning py-2 small">
                  The extension was just updated. <button className="btn btn-link btn-sm p-0 align-baseline" onClick={() => window.location.reload()}>Reload this page</button>{" "}
                  to connect it.
                </div>
              )}
              {state === "timeout" && !stale && (
                <div className="alert alert-warning py-2 small">
                  The extension didn't respond. Reload this page and try again.
                </div>
              )}
              <button className="btn btn-primary" onClick={connect} disabled={state === "connecting"}>
                {state === "connecting" && <span className="spinner-border spinner-border-sm me-2" />}
                Connect extension
              </button>
            </>
          ) : extensionUrl ? (
            <>
              <h2 className="h6">Add Plateo to Chrome</h2>
              <p className="text-muted small">
                Install it from the Chrome Web Store. Once it's added, Plateo opens a new tab where you connect it to your account.
              </p>
              <div className="d-flex flex-wrap gap-2">
                <a className="btn btn-primary" href={extensionUrl} target="_blank" rel="noreferrer">
                  <i className="bi bi-browser-chrome me-1" /> Add to Chrome
                </a>
                <button className="btn btn-light" onClick={() => window.location.reload()}>
                  <i className="bi bi-arrow-clockwise me-1" /> I installed it, reload
                </button>
              </div>
              <p className="text-muted small mt-3 mb-0">
                Pin Plateo from the puzzle-piece menu so its icon stays in the toolbar. It works in Chrome, Edge and Brave on a computer.
              </p>
            </>
          ) : process.env.NODE_ENV === "production" ? (
            <div className="text-center py-3">
              <div className="success-icon mb-3">
                <i className="bi bi-browser-chrome" />
              </div>
              <h2 className="h5">Chrome extension coming soon</h2>
              <p className="text-muted small mb-0">
                The Plateo extension is waiting for approval on the Chrome Web Store. Once it's live, you'll be able to add it
                from this page. Everything else in Plateo works in the meantime.
              </p>
            </div>
          ) : (
            <>
              <h2 className="h6">Install the extension (about a minute)</h2>
              <ol className="small mb-3 ps-3">
                <li className="mb-1">
                  In Chrome, open <code>chrome://extensions</code> and turn on <strong>Developer mode</strong> (top right).
                </li>
                <li className="mb-1">
                  Click <strong>Load unpacked</strong> and choose the <code>extension</code> folder inside the Plateo project.
                </li>
                <li className="mb-1">Pin Plateo from the puzzle-piece menu so its icon stays in the toolbar.</li>
                <li>Come back here and reload this page.</li>
              </ol>
              <button className="btn btn-light btn-sm" onClick={() => window.location.reload()}>
                <i className="bi bi-arrow-clockwise me-1" /> I installed it, reload
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default ConnectExtension;
